'use client';
import { FC } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Box, Typography } from '@mui/material';
import { SampleDto } from '@/api/ApiClient';
import { formatDate } from '@/lib/utils/dateUtils';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';

export const selectOptions: OptionKey[] = ['materials', 'synthesisTechniques'];

interface StartingMaterialDetailsDialogProps {
  open: boolean;
  startingMaterial: SampleDto | null;
  onClose: () => void;
}

const StartingMaterialDetailsDialog: FC<StartingMaterialDetailsDialogProps> = ({ open, startingMaterial, onClose }) => {
  const { resolveUser } = useUserResolution();
  const { options } = useSelectOptions(selectOptions);

  const getTechniqueName = (id: string) => options.synthesisTechniques?.find((m) => m.id === id)?.name || id;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle>
        StartingMaterial Sample Details
        {startingMaterial ? ` - ${startingMaterial.sampleLabel}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {startingMaterial ? (
          <Stack spacing={3}>
            {/* General Information */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                General Information
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                    Sample Label:
                  </Typography>
                  <Typography variant="body2">{startingMaterial.sampleLabel}</Typography>
                </Box>
                {startingMaterial.refSampleLabel ? (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                      Alias:
                    </Typography>
                    <Typography variant="body2">{startingMaterial.refSampleLabel}</Typography>
                  </Box>
                ) : null}
                {startingMaterial.techniqueId ? (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                      Technique:
                    </Typography>
                    <Typography variant="body2">{getTechniqueName(startingMaterial.techniqueId)}</Typography>
                  </Box>
                ) : null}
                {startingMaterial.userId ? (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                      Made by:
                    </Typography>
                    <Typography variant="body2">{resolveUser(startingMaterial.userId)}</Typography>
                  </Box>
                ) : null}
                {startingMaterial.creationDate ? (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                      Inventory Date:
                    </Typography>
                    <Typography variant="body2">{formatDate(startingMaterial.creationDate)}</Typography>
                  </Box>
                ) : null}
                {startingMaterial.projectLabels && startingMaterial.projectLabels.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                      Projects:
                    </Typography>
                    <Typography variant="body2">{startingMaterial.projectLabels.join(', ')}</Typography>
                  </Box>
                ) : null}
              </Stack>
            </Box>

            {/* Materials */}
            {startingMaterial.materials && startingMaterial.materials.length > 0 ? (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Materials
                </Typography>
                <Typography
                  variant="body2"
                  dangerouslySetInnerHTML={{
                    __html: startingMaterial.materials.join(', '),
                  }}
                />
              </Box>
            ) : null}

            {/* Substrate Information */}
            {startingMaterial.substrateName ||
            startingMaterial.substrateVendor ||
            (startingMaterial.substrateMaterials && startingMaterial.substrateMaterials.length > 0) ? (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Substrate Details
                </Typography>
                <Stack spacing={1}>
                  {startingMaterial.substrateName ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                        Name:
                      </Typography>
                      <Typography variant="body2">{startingMaterial.substrateName}</Typography>
                    </Box>
                  ) : null}
                  {startingMaterial.substrateVendor ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                        Vendor:
                      </Typography>
                      <Typography variant="body2">{startingMaterial.substrateVendor}</Typography>
                    </Box>
                  ) : null}
                  {startingMaterial.substrateLot ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                        Lot:
                      </Typography>
                      <Typography variant="body2">{startingMaterial.substrateLot}</Typography>
                    </Box>
                  ) : null}
                  {startingMaterial.substrateOrientation ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                        Orientation:
                      </Typography>
                      <Typography variant="body2">{startingMaterial.substrateOrientation}</Typography>
                    </Box>
                  ) : null}
                  {startingMaterial.substrateMaterials && startingMaterial.substrateMaterials.length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                        Material:
                      </Typography>
                      <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                          __html: startingMaterial.substrateMaterials.join(', '),
                        }}
                      />
                    </Box>
                  ) : null}
                </Stack>
              </Box>
            ) : null}

            {/* Description */}
            {startingMaterial.description && startingMaterial.description !== '<br>' ? (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  dangerouslySetInnerHTML={{
                    __html: startingMaterial.description,
                  }}
                />
              </Box>
            ) : null}

            {/* Additional Properties */}
            {startingMaterial.quality || startingMaterial.size || startingMaterial.thickness ? (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Physical Properties
                </Typography>
                <Stack spacing={1}>
                  {startingMaterial.quality ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                        Quality:
                      </Typography>
                      <Typography variant="body2">{startingMaterial.quality}</Typography>
                    </Box>
                  ) : null}
                  {startingMaterial.size ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                        Size:
                      </Typography>
                      <Typography variant="body2">{startingMaterial.size}</Typography>
                    </Box>
                  ) : null}
                  {startingMaterial.thickness ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                        Thickness:
                      </Typography>
                      <Typography variant="body2">{startingMaterial.thickness}</Typography>
                    </Box>
                  ) : null}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartingMaterialDetailsDialog;
